import {Thuisverzorger} from '../../thuisverzorger/thuisverzorger';
import {Regioverantwoordelijke} from '../../regioverantwoordelijke/regioverantwoordelijke';
import {Therapeut} from '../../therapeut/therapeut';

export default class User {

  static fromJwt(jwt: JwtPayload, voornaam?: string, achternaam?: string, personeelsnummer?: string): User {
    if (!!jwt) {
      return new User(
        true,
        jwt.sub,
        voornaam,
        achternaam,
        personeelsnummer,
        jwt.email,
        (jwt['cognito:groups'] || [])
          .map(group => Role[group.toUpperCase()])
          .filter(role => !!role),
      );
    } else {
      return User.anonymousUser();
    }
  }

  static anonymousUser(): User {
    return new User(
      false,
      null,
      'Anoniem',
      'Incognito',
      'Onbekend',
      null,
      [],
    );
  }

  static fromThuisverzorger(thuisverzorger: Thuisverzorger): User {
    return new User(
      false,
      thuisverzorger.getId(),
      thuisverzorger.getVoornaam(),
      thuisverzorger.getAchternaam(),
      thuisverzorger.getPersoneelsnummer(),
      thuisverzorger.getEmail(),
      [Role.THUISVERZORGER],
      thuisverzorger.isVerwijderd(),
    );
  }

  static fromRegioverantwoordelijke(regioverantwoordelijke: Regioverantwoordelijke): User {
    return new User(
      false,
      regioverantwoordelijke.getId(),
      regioverantwoordelijke.getVoornaam(),
      regioverantwoordelijke.getAchternaam(),
      regioverantwoordelijke.getPersoneelsnummer(),
      regioverantwoordelijke.getEmail(),
      [Role.REGIOVERANTWOORDELIJKE],
      regioverantwoordelijke.isVerwijderd(),
    );
  }

  static fromTherapeut(therapeut: Therapeut): User {
    return new User(
      false,
      therapeut.getId(),
      therapeut.getVoornaam(),
      therapeut.getAchternaam(),
      therapeut.getPersoneelsnummer(),
      therapeut.getEmail(),
      [Role.THERAPEUT],
      therapeut.isVerwijderd(),
    );
  }

  constructor(public authenticated: boolean,
              public id: string,
              public voornaam: string,
              public achternaam: string,
              public personeelsnummer: string,
              public email: string,
              public roles: Role[],
              public verwijderd: boolean = false,
  ) {
  }

  isAuthenticated(): boolean {
    return this.authenticated;
  }

  getId(): string {
    return this.id;
  }

  hasId(id: string): boolean {
    return this.getId() === id;
  }

  isSuperAdmin(): boolean {
    return this.hasRole(Role.SUPERADMIN);
  }

  isAdmin(): boolean {
    return this.hasRole(Role.ADMIN);
  }

  isRegioverantwoordelijke(): boolean {
    return this.hasRole(Role.REGIOVERANTWOORDELIJKE);
  }

  isTherapeut(): boolean {
    return this.hasRole(Role.THERAPEUT);
  }

  isThuisverzorger(): boolean {
    return this.hasRole(Role.THUISVERZORGER);
  }

  hasRole(role: Role): boolean {
    return this.roles.includes(role);
  }

  hasOneOfRoles(roles: Array<Role>): boolean {
    return !!roles.find(role => this.hasRole(role));
  }

  getFirstRole(): Role {
    return allRoles.find(role => this.hasRole(role));
  }

  getRolesString(): string {
    return this.roles.join(', ');
  }

  getVoornaam(): string {
    return this.voornaam;
  }

  getAchternaam(): string {
    return this.achternaam;
  }

  getNaam(): string {
    return `${this.getAchternaam()} ${this.getVoornaam()}`;
  }

  getPersoneelsnummer(): string {
    return this.personeelsnummer;
  }

  getUsername(): string {
    return this.voornaam ? this.voornaam : this.email.substr(0, this.email.indexOf('@'));
  }

  isVerwijderd(): boolean {
    return this.verwijderd;
  }
}

export enum Role {
  SUPERADMIN = 'Superadmin',
  ADMIN = 'Admin',
  REGIOVERANTWOORDELIJKE = 'Regioverantwoordelijke',
  THERAPEUT = 'Therapeut',
  THUISVERZORGER = 'Thuisverzorger',
}

const allRoles: Array<Role> = [Role.SUPERADMIN, Role.ADMIN, Role.REGIOVERANTWOORDELIJKE, Role.THERAPEUT, Role.THUISVERZORGER];

export interface JwtPayload {
  sub: string;
  email: string;
  email_verified: boolean;
  'cognito:groups': string[];
}
